import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Button, Form, Dropdown,
} from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import './PublishModal.css';

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  key: i + 1,
  text: String(i + 1),
  value: i + 1,
}));

const MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  key: i * 5,
  text: String(i * 5).padStart(2, '0'),
  value: i * 5,
}));

const PERIOD_OPTIONS = [
  { key: 'am', text: 'AM', value: 'AM' },
  { key: 'pm', text: 'PM', value: 'PM' },
];

const getTomorrow = () => moment().add(1, 'day').startOf('day');

const ACTIVATION_MIN_FUTURE_MINUTES = 5;
const ACTIVATION_MIN_FUTURE_ERROR = `Activation date and time must be at least ${ACTIVATION_MIN_FUTURE_MINUTES} minutes in the future.`;

const PublishModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  error,
  productName,
  productType,
}) => {
  const [activationDate, setActivationDate] = useState(getTomorrow);
  const [hour, setHour] = useState(1);
  const [minute, setMinute] = useState(0);
  const [period, setPeriod] = useState('AM');
  const [validationError, setValidationError] = useState('');

  const colorClass = productType === 'seller-assurance' ? 'theme-green' : 'theme-blue';

  const resetForm = () => {
    setActivationDate(getTomorrow());
    setHour(1);
    setMinute(0);
    setPeriod('AM');
    setValidationError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleConfirm = () => {
    let hours24 = hour;
    if (period === 'AM' && hour === 12) hours24 = 0;
    if (period === 'PM' && hour !== 12) hours24 = hour + 12;

    const combined = moment(activationDate)
      .hours(hours24)
      .minutes(minute)
      .seconds(0)
      .milliseconds(0);

    if (!combined.isAfter(moment().add(ACTIVATION_MIN_FUTURE_MINUTES, 'minutes'))) {
      setValidationError(ACTIVATION_MIN_FUTURE_ERROR);
      return;
    }

    setValidationError('');
    onConfirm(combined.format('YYYY-MM-DDTHH:mm:ss'));
  };

  const displayedError = validationError || error;

  return (
    <Modal
      open={open}
      closeIcon
      onClose={handleClose}
      closeOnEscape={false}
      closeOnRootNodeClick={false}
      size='small'
      className={`publish-modal theme ${colorClass}`}
    >
      <Modal.Header>Publish Rules</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <p className='publish-modal__intro'>
            Schedule publishing for <strong>{productName}</strong> with
            activation date and time.
          </p>
          <p className='publish-modal__product'>
            Product: <strong>{productName}</strong>
          </p>
          <Form>
            <Form.Field>
              <label htmlFor='activation-date'>Activation Date</label>
              <DatePicker
                id='activation-date'
                selected={activationDate}
                onChange={date => setActivationDate(date)}
                minDate={moment()}
                dateFormat='MM/DD/YYYY'
              />
            </Form.Field>
            <Form.Field>
              <label htmlFor='activation-hour'>Time</label>
              <div className='publish-modal__time-picker'>
                <Dropdown
                  id='activation-hour'
                  selection
                  options={HOUR_OPTIONS}
                  value={hour}
                  onChange={(_, d) => setHour(d.value)}
                  className='publish-modal__time-hour'
                />
                <span className='publish-modal__time-separator'>:</span>
                <Dropdown
                  selection
                  options={MINUTE_OPTIONS}
                  value={minute}
                  onChange={(_, d) => setMinute(d.value)}
                  className='publish-modal__time-minute'
                />
                <Dropdown
                  selection
                  options={PERIOD_OPTIONS}
                  value={period}
                  onChange={(_, d) => setPeriod(d.value)}
                  className='publish-modal__time-period'
                />
              </div>
            </Form.Field>
            {displayedError && (
              <p className='publish-modal__error'>{displayedError}</p>
            )}
          </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          className={`action-button primary ${colorClass}`}
          onClick={handleConfirm}
          disabled={loading}
          loading={loading}
        >
          Publish
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

PublishModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  productName: PropTypes.string.isRequired,
  productType: PropTypes.string.isRequired,
};

PublishModal.defaultProps = {
  error: '',
};

export default PublishModal;
